import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ScanSearch, Plus, Loader2, AlertTriangle, Plus as PlusIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_TYPES = [
  { value: 'home', label: 'Home' },
  { value: 'categoria', label: 'Categoria' },
  { value: 'produto', label: 'Produto' },
  { value: 'busca', label: 'Busca' },
  { value: 'outra', label: 'Outra' },
];

const STATUS_LABELS = {
  processando: { label: 'Processando', variant: 'secondary' },
  rascunho_ia: { label: 'Rascunho IA', variant: 'default' },
  em_revisao: { label: 'Em revisão', variant: 'default' },
  revisado: { label: 'Revisado', variant: 'default' },
  erro: { label: 'Erro', variant: 'destructive' },
};

export const Diagnostics = () => {
  const { selectedProject } = useApp();
  const navigate = useNavigate();
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [ga4Msg, setGa4Msg] = useState(null);
  const [selected, setSelected] = useState({});   // url -> {url, page_type, source}
  const [manualUrl, setManualUrl] = useState('');
  const [loadingSug, setLoadingSug] = useState(false);
  const [creating, setCreating] = useState(false);
  const [needsSiteUrl, setNeedsSiteUrl] = useState(false);

  const load = useCallback(async () => {
    if (!selectedProject) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/diagnostics/projects/${selectedProject.id}`);
      setDiagnostics(res.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => { load(); }, [load]);

  const openDialog = async () => {
    setSelected({}); setManualUrl(''); setNeedsSiteUrl(false); setGa4Msg(null); setSuggestions([]);
    setDialogOpen(true);
    setLoadingSug(true);
    try {
      const res = await axios.get(`${API_URL}/api/diagnostics/projects/${selectedProject.id}/page-suggestions`);
      setSuggestions(res.data.suggestions || []);
      setGa4Msg(res.data.message);
      const pre = {};
      (res.data.suggestions || []).forEach((s) => {
        if (s.preselected) pre[s.url] = { url: s.url, page_type: s.page_type, source: 'ga4' };
      });
      setSelected(pre);
    } catch (e) {
      if (e.response?.status === 400) { setNeedsSiteUrl(true); }
      else console.error(e);
    } finally { setLoadingSug(false); }
  };

  const toggle = (s) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[s.url]) { delete next[s.url]; }
      else {
        if (Object.keys(next).length >= 4) { toast.error('Máximo de 4 páginas.'); return prev; }
        next[s.url] = { url: s.url, page_type: s.page_type, source: s.source };
      }
      return next;
    });
  };

  const setType = (url, page_type) => {
    setSelected((prev) => ({ ...prev, [url]: { ...prev[url], page_type } }));
  };

  const addManual = () => {
    let u = manualUrl.trim();
    if (!u.startsWith('https://')) { toast.error('A URL deve começar com https://'); return; }
    if (selected[u]) { toast.info('URL já adicionada.'); return; }
    if (Object.keys(selected).length >= 4) { toast.error('Máximo de 4 páginas.'); return; }
    setSelected((prev) => ({ ...prev, [u]: { url: u, page_type: 'outra', source: 'manual' } }));
    setManualUrl('');
  };

  const create = async () => {
    const pages = Object.values(selected);
    if (pages.length === 0) { toast.error('Selecione ao menos uma página.'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/diagnostics/projects/${selectedProject.id}`, { project_id: selectedProject.id, pages });
      setDialogOpen(false);
      navigate(`/diagnostics/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar diagnóstico');
    } finally { setCreating(false); }
  };

  if (!selectedProject) {
    return <div className="text-center py-12"><p className="text-muted-foreground">Selecione um projeto para ver os diagnósticos.</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="diagnostics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><ScanSearch className="h-7 w-7 text-primary" /> Diagnóstico de Usabilidade</h1>
          <p className="text-sm text-muted-foreground mt-1">Desempenho (PageSpeed) e, em breve, pré-análise de usabilidade por IA.</p>
        </div>
        <Button onClick={openDialog} data-testid="new-diagnostic-btn"><Plus className="h-4 w-4 mr-2" /> Novo diagnóstico</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : diagnostics.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="diagnostics-empty">Nenhum diagnóstico ainda. Crie o primeiro.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {diagnostics.map((d) => {
            const st = STATUS_LABELS[d.status] || { label: d.status, variant: 'secondary' };
            return (
              <Card key={d.id} className="cursor-pointer hover:border-primary/40 transition-colors" onClick={() => navigate(`/diagnostics/${d.id}`)} data-testid={`diagnostic-row-${d.id}`}>
                <CardContent className="py-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">{new Date(d.created_at).toLocaleString('pt-BR')}</p>
                    <p className="text-sm text-muted-foreground">{(d.pages || []).length} página(s) · {d.author_name || '—'}</p>
                  </div>
                  <Badge variant={st.variant}>{st.label}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="new-diagnostic-dialog">
          <DialogHeader><DialogTitle>Selecionar páginas (até 4)</DialogTitle></DialogHeader>
          {needsSiteUrl ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600" />
              <span>Preencha a <b>URL do site</b> do projeto (em Todos os projetos → editar) antes de criar um diagnóstico.</span>
            </div>
          ) : (
            <div className="space-y-4 max-h-[55vh] overflow-y-auto">
              {loadingSug ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : (
                <>
                  {ga4Msg && (
                    <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700" data-testid="ga4-inactive-warning">
                      <AlertTriangle className="h-3.5 w-3.5" /> {ga4Msg}
                    </div>
                  )}
                  {suggestions.map((s) => (
                    <div key={s.url} className="flex items-center gap-3 border-b pb-2" data-testid={`suggestion-${s.path}`}>
                      <Checkbox checked={!!selected[s.url]} onCheckedChange={() => toggle(s)} data-testid={`suggestion-check-${s.path}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.path}</p>
                        <p className="text-xs text-muted-foreground">{s.views?.toLocaleString('pt-BR')} visualizações</p>
                      </div>
                      {selected[s.url] && (
                        <Select value={selected[s.url].page_type} onValueChange={(v) => setType(s.url, v)}>
                          <SelectTrigger className="w-[130px] h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>{PAGE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                  {/* selected manual entries */}
                  {Object.values(selected).filter((x) => x.source === 'manual').map((x) => (
                    <div key={x.url} className="flex items-center gap-3 border-b pb-2">
                      <Checkbox checked onCheckedChange={() => toggle({ url: x.url })} />
                      <p className="flex-1 min-w-0 text-sm truncate">{x.url}</p>
                      <Select value={x.page_type} onValueChange={(v) => setType(x.url, v)}>
                        <SelectTrigger className="w-[130px] h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{PAGE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <Input placeholder="https://..." value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} data-testid="manual-url-input" />
                    <Button variant="outline" size="sm" onClick={addManual} data-testid="add-manual-url-btn"><PlusIcon className="h-4 w-4" /></Button>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <span className="text-sm text-muted-foreground mr-auto">{Object.keys(selected).length}/4 selecionadas</span>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={create} disabled={creating || needsSiteUrl || Object.keys(selected).length === 0} data-testid="confirm-create-diagnostic-btn">
              {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Criando...</> : 'Analisar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
